import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  AngleRight,
  ChakraBox,
  ChakraFlex,
  ChakraMenu,
  ChakraMenuButton,
  ChakraMenuItem,
  ChakraMenuList,
  Divider,
  EquipUITheme,
  FAIconName,
  FireflyIconButton,
  Icon,
  Popover,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { AssessmentEvents, NoteEvents } from '~/analytics';
import {
  AssessmentEventAttribute,
  AssessmentEventSource,
} from '~/analytics/assessmentEvents';
import { PatientActionEvents } from '~/analytics/patientActionEvents';
import Scheduler from '~/components/common/calendar/Scheduler';
import ConfirmModal from '~/components/common/ConfirmModal';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { SCHEDULE_APPOINTMENT_OPTION } from '~/lib/constants/appointment.constants';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import {
  AssessmentAction,
  GENERATE_PATIENT_REPORT,
  ORDER_LAB_TEXT,
} from '~/lib/constants/patients.constants';
import {
  diagnosisCarerSurveyNames,
  diagnosisClinicianSurveyNames,
  generalCarerSurveyNames,
  generalClinicianSurveyNames,
} from '~/lib/constants/survey/CategorizedSurveyNames';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyNameToDisplayNameMap } from '~/lib/constants/survey/SurveyNameToDisplayNameMap';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useAppointmentBookingEligibility from '~/lib/hooks/useAppointmentBookingEligibility';
import useAppointments from '~/lib/hooks/useAppointments';
import useErrorToast from '~/lib/hooks/useErrorToast';
import {
  ClinicianAssessmentsSurveyCards,
  isSurveyRestrictedByDiagnosis,
} from '~/lib/util/survey.util';
import {
  onRecordNewSurvey,
  onSendNewSurvey,
} from '~/lib/util/surveyActions.util';
import util from '~/lib/util/util';
import { useAppointmentDispatch } from '~/store/reducers/appointment';
import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';
import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import { OrderLabDrawer } from '../tabs/labs/OrderLabDrawer';
import { shouldShowOrderLabButton } from '~/lib/util/patient.util';
import LabOrderActionBar from '../common/LabOrderActionBar';
import { getEmptyLabOrderFields } from '~/lib/util/lab.util';
import { setShowOrders } from '~/store/reducers/patientLabTab';
import { useAppDispatch } from '~/store/hooks';
import { useGetSurveysQuery } from '~/store/services/assessments';
import Loading from '~/pages/loading';
import { IconTextMenuButtonOption } from '~/components/common/IconTextMenuButtonOption';
import { useDocumentUpload } from '~/lib/hooks/usePatientDocumentUpload';
import UploadConfirmationModal from '../common/UploadConfirmationModal';
import { UPLOAD_DOCUMENT } from '~/lib/constants/documentCenter.constants';
import { CREATE_TASK_SOURCES } from '~/lib/constants/task.constants';
import GeneratePatientReportModal from './GeneratePatientReportModal';

const { paragraph } = EquipUITheme.typography;

const TOP_LEVEL_ID = 'actions-cta';

interface ActionsCtaProps {
  patient: Patient;
  patientClinicalProfile?: PatientClinicalProfile;
  patientPreferredFullName: string;
  updateAssessmentState: Dispatch<AssessmentAction>;
}

const ActionsCta = ({
  patient,
  patientClinicalProfile,
  patientPreferredFullName,
  updateAssessmentState,
}: ActionsCtaProps) => {
  const { trackEvent } = useAnalytics();
  const { currentUser } = useGlobalContext();
  const { setAppointmentCurrentDate } = useAppointmentDispatch();
  const { setComponentVisibility } = useComponentPortalContext();
  const { openNoteDrawer } = useEditNoteDrawerContext();
  const { isOnPatientProfile } = useGlobalContext();
  const { createToast } = useToastMessage();
  const [isUploadSuccessModalOpen, setIsUploadSuccessModalOpen] =
    useState(false);
  const [isGenerateReportModalOpen, setIsGenerateReportModalOpen] =
    useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileUploadErrorMessages, setFileUploadErrorMessages] = useState<
    string[]
  >([]);

  const { openFileExplorer } = useDocumentUpload({
    onUploadSuccess: (files, modalErrorMessages) => {
      if (files.length === 0) return;
      setFileUploadErrorMessages(modalErrorMessages);
      setUploadedFiles(files);
      setIsUploadSuccessModalOpen(true);
    },
    patientId: patient.userId,
  });
  const { docGenTreatmentSummary: isDocGenTreatmentSummaryEnabled } =
    useFlags<LDFlags>();

  const { data: patientDiagnoses } = useGetPatientDiagnosesQuery(
    patient.userId,
  );

  const { data: surveys, isLoading: isGetSurveysLoading } = useGetSurveysQuery({
    pageSize: 100,
  });

  const dispatch = useAppDispatch();
  const [isGeneralAssessmentPopOverOpen, setIsGeneralAssessmentPopOverOpen] =
    useState<boolean>(false);
  const [isScheduleAppointmentsOpen, setScheduleAppointmentsOpen] =
    useState<boolean>(false);
  const [isOrderLabDrawerOpen, setIsOrderLabDrawerOpen] =
    useState<boolean>(false);
  const [
    isConfirmScheduleAppointmentModalOpen,
    setIsConfirmScheduleAppointmentModalOpen,
  ] = useState<boolean>(false);

  const [
    isDiagnosisAssessmentPopOverOpen,
    setIsDiagnosisAssessmentPopOverOpen,
  ] = useState<boolean>(false);

  const [isOverrideModalOpen, setOpenOverrideModal] = useState<boolean>(false);
  const [surveyToBeSent, setSurveyToBeSent] = useState<SurveyName>(null);

  const [isAnyAppointmentTypeSelected, setIsAnyAppointmentTypeSelected] =
    useState<boolean>(false);

  const {
    patientCareTeams,
    getProviderTeamCareTeam,
    hasSchedulingAuthorization,
  } = useAppointments();

  const { data: treatmentEpisodesData } = useGetPatientTreatmentEpisodesQuery(
    patient?.userId,
  );

  const { showErrorToast } = useErrorToast();
  const [schedulerKey, setSchedulerKey] = useState<number>(0);
  const [showActionBar, setShowActionBar] = useState<boolean>(false);
  const patientIdRef = useRef<string | null>(null);

  const getPatientCareTeam = useCallback(() => {
    patientIdRef.current = patient.userId;
    getProviderTeamCareTeam(patient.userId);
  }, [patient.userId, getProviderTeamCareTeam]);

  useEffect(() => {
    if (patient && !patientCareTeams) {
      getPatientCareTeam();
    }
  }, [patient, patientCareTeams, getPatientCareTeam]);

  useEffect(() => {
    if (
      patientIdRef.current !== null &&
      patient.userId !== patientIdRef.current
    ) {
      getPatientCareTeam();
    }
  }, [patient.userId, getPatientCareTeam]);

  const { isBookingEnabled } = useAppointmentBookingEligibility({
    patient,
    treatmentEpisodes: treatmentEpisodesData?.data,
  });

  const handleConfirmClick = () => {
    onSendNewSurvey(surveyToBeSent, updateAssessmentState);
    setOpenOverrideModal(false);
    setSurveyToBeSent(null);
  };

  const handleSendNewSurvey = (surveyName: SurveyName) => {
    const survey = surveys.surveys.find((s) => s.name === surveyName);
    if (!survey) {
      createToast({
        id: 'survey-not-found-handle-send-new-survey',
        text: 'Unexpected error occurred. Please try refreshing the page.',
        variant: 'critical',
      });
      return;
    }
    if (
      isSurveyRestrictedByDiagnosis(survey.jsonParameters, patientDiagnoses)
    ) {
      setOpenOverrideModal(true);
      setSurveyToBeSent(surveyName);
    } else {
      onSendNewSurvey(surveyName, updateAssessmentState);
    }
  };

  const getAssessmentPopOverOptions = (assessmentOptions: SurveyName[]) => (
    <>
      {assessmentOptions.map((surveyName) => (
        <ChakraBox
          {...paragraph}
          _hover={{ background: 'gray.100' }}
          cursor="pointer"
          id={`${TOP_LEVEL_ID}__list-option__${surveyName
            .replaceAll(' ', '')
            .toLowerCase()}`}
          key={surveyName.replaceAll(' ', '').toLowerCase()}
          onClick={() => {
            if (ClinicianAssessmentsSurveyCards.includes(surveyName)) {
              onRecordNewSurvey(surveyName, updateAssessmentState);
            } else {
              handleSendNewSurvey(surveyName);
            }
            trackEvent(AssessmentEvents.createNewAssessmentClicked, {
              [AssessmentEventAttribute.SurveyName]: surveyName,
              [AssessmentEventAttribute.Source]:
                AssessmentEventSource.PatientHeaderActionsMenu,
            });
          }}
          padding="8px 4px"
        >
          <Text variant="body">{SurveyNameToDisplayNameMap[surveyName]}</Text>
        </ChakraBox>
      ))}
    </>
  );

  const assessmentOptionsRenderer = (
    clinicianOptions: SurveyName[],
    carerOptions: SurveyName[],
  ) => (
    <ChakraBox maxHeight="540px">
      {!util.isEmptyArray(clinicianOptions) && (
        <>
          <ChakraBox
            alignItems="center"
            display="flex"
            gap="12px"
            padding="8px 4px"
          >
            <Text color="neutral.secondary" variant="caption">
              Clinician
            </Text>
            <Divider id="general-assessment" width="100%" />
          </ChakraBox>

          {getAssessmentPopOverOptions(clinicianOptions)}
        </>
      )}
      <ChakraBox
        alignItems="center"
        display="flex"
        gap="12px"
        padding="8px 4px"
      >
        <Text color="neutral.secondary" variant="caption" width="70%">
          Patient and carer
        </Text>
        <Divider id="diagnosis-assessment" width="100%" />
      </ChakraBox>
      {getAssessmentPopOverOptions(carerOptions)}
    </ChakraBox>
  );

  const assessmentTrigger = (
    title: string,
    onMouseMove: (value: boolean) => void,
  ) => (
    <ChakraFlex
      alignItems="center"
      cursor="pointer"
      gap="12px"
      id={`${TOP_LEVEL_ID}__assessment-title-${title
        .replaceAll(' ', '')
        .toLowerCase()}`}
      onMouseEnter={() => {
        // ensure only one popover is open
        setIsGeneralAssessmentPopOverOpen(false);
        setIsDiagnosisAssessmentPopOverOpen(false);
        onMouseMove(true);
      }}
      width="100%"
    >
      <Icon
        name={
          title === 'General assessment'
            ? FAIconName.clipboardListRegular
            : FAIconName.clipboardMedicalRegular
        }
      />
      <Text>{title}</Text>

      <ChakraBox
        height="16px"
        marginInlineStart="auto"
        marginTop="5px"
        width="16px"
      >
        <AngleRight />
      </ChakraBox>
    </ChakraFlex>
  );

  const onSchedulerClose = (date?: string): void => {
    if (date) {
      setAppointmentCurrentDate(DateTime.fromISO(date).toString());
    }
    setScheduleAppointmentsOpen(false);
    setSchedulerKey((key) => key + 1);
  };

  const onSchedulerError = (
    _: string,
    message: string,
    statusCode?: number,
  ): void => {
    onSchedulerClose();
    showErrorToast(GlobalErrorCode.SAVE_APPOINTMENT, statusCode, message);
  };

  const handleScheduleAppointmentClick = () => {
    setScheduleAppointmentsOpen(true);
    trackEvent(
      PatientActionEvents.scheduleAppointmentsActionMenuV2ButtonClicked,
      {
        Source: AnalyticsSourceValues.ACTION_MENU,
      },
    );
  };

  const handleLabOrderSubmittedActionCta = async () => {
    dispatch(setShowOrders(true));
    setShowActionBar(true);
    setTimeout(() => setShowActionBar(false), 8000);
  };

  const closeSchedulerModal = () => {
    setIsAnyAppointmentTypeSelected(false);
    setIsConfirmScheduleAppointmentModalOpen(false);
    setScheduleAppointmentsOpen(false);
    onSchedulerClose();
  };

  const handleSchedulerModalClose = (
    _: string,
    shouldShowWarningModal: boolean,
  ) => {
    if (isAnyAppointmentTypeSelected) {
      if (shouldShowWarningModal !== false) {
        setIsConfirmScheduleAppointmentModalOpen(true);
      } else {
        closeSchedulerModal();
      }
    } else {
      closeSchedulerModal();
    }
  };

  const {
    activeGeneralClinician,
    activeGeneralCarer,
    activeDiagnosisClinician,
    activeDiagnosisCarer,
  } = useMemo(() => {
    const activeNames = new Set(
      (surveys?.surveys ?? [])
        .filter((s) => s.status === 'Active')
        .map((s) => s.name),
    );
    return {
      activeGeneralClinician: generalClinicianSurveyNames.filter((n) =>
        activeNames.has(n),
      ),
      activeGeneralCarer: generalCarerSurveyNames.filter((n) =>
        activeNames.has(n),
      ),
      activeDiagnosisClinician: diagnosisClinicianSurveyNames.filter((n) =>
        activeNames.has(n),
      ),
      activeDiagnosisCarer: diagnosisCarerSurveyNames.filter((n) =>
        activeNames.has(n),
      ),
    };
  }, [surveys]);

  if (isGetSurveysLoading) {
    return <Loading />;
  }

  return (
    <>
      <ChakraMenu id={TOP_LEVEL_ID}>
        <ChakraMenuButton
          ariaLabel="Actions"
          as={FireflyIconButton}
          borderRadius="25px"
          icon={FAIconName.plusSolid}
          padding="14px"
          width="48px"
        />

        <ChakraMenuList padding="14px 10px" zIndex={101}>
          <ChakraFlex direction="column" gap="4px">
            <ChakraMenuItem>
              <Popover
                bodyContent={assessmentOptionsRenderer(
                  activeGeneralClinician,
                  activeGeneralCarer,
                )}
                id={`${TOP_LEVEL_ID}__general-assessment`}
                isOpen={isGeneralAssessmentPopOverOpen}
                margin="100px 0px 20px 0px"
                maxHeight="540px"
                onClose={() => setIsGeneralAssessmentPopOverOpen(false)}
                placement="left"
                size="sm"
                triggerContent={assessmentTrigger(
                  'General assessment',
                  setIsGeneralAssessmentPopOverOpen,
                )}
              />
            </ChakraMenuItem>

            <ChakraMenuItem>
              <Popover
                bodyContent={assessmentOptionsRenderer(
                  activeDiagnosisClinician,
                  activeDiagnosisCarer,
                )}
                id={`${TOP_LEVEL_ID}__diagnosis-assessment`}
                isOpen={isDiagnosisAssessmentPopOverOpen}
                margin="120px 0px 20px 0px"
                maxHeight="540px"
                onClose={() => setIsDiagnosisAssessmentPopOverOpen(false)}
                placement="left"
                size="sm"
                triggerContent={assessmentTrigger(
                  'Diagnosis assessment',
                  setIsDiagnosisAssessmentPopOverOpen,
                )}
              />
            </ChakraMenuItem>
            <ChakraMenuItem
              onClick={() => {
                trackEvent(PatientActionEvents.addTaskActionMenuButtonClicked, {
                  Source: AnalyticsSourceValues.ACTION_MENU,
                  patientId: patient.userId,
                });
                setComponentVisibility({
                  component: PortalComponent.ADD_TASK_ACCORDION,
                  isExpanded: true,
                  isVisible: true,
                  showLinkedPatient: true,
                  data: {
                    triggerSource: CREATE_TASK_SOURCES.ACTION_MENU,
                  },
                });
              }}
            >
              <IconTextMenuButtonOption
                iconName={FAIconName.checkSquareRegular}
                title="Add task"
              />
            </ChakraMenuItem>
            {isOnPatientProfile && (
              <ChakraMenuItem
                onClick={() => {
                  trackEvent(NoteEvents.addNoteActionMenuButtonClicked, {
                    Source: 'Action Menu',
                  });
                  openNoteDrawer(patient.userId, null);
                }}
                style={{ margin: 0 }}
              >
                <IconTextMenuButtonOption
                  iconName={FAIconName.fileRegular}
                  title="Add note"
                />
              </ChakraMenuItem>
            )}
            <ChakraMenuItem
              onClick={() => {
                trackEvent(
                  PatientActionEvents.uploadDocumentActionMenuButtonClicked,
                  {
                    Source: AnalyticsSourceValues.ACTION_MENU,
                  },
                );
                openFileExplorer();
              }}
              style={{ margin: 0 }}
            >
              <IconTextMenuButtonOption
                iconName={FAIconName.fileArrowUpRegular}
                title={UPLOAD_DOCUMENT}
              />
            </ChakraMenuItem>
            {isDocGenTreatmentSummaryEnabled && (
              <ChakraMenuItem
                onClick={() => {
                  trackEvent(
                    PatientActionEvents.generatePatientReportActionMenuButtonClicked,
                    {
                      Source: AnalyticsSourceValues.ACTION_MENU,
                    },
                  );
                  setIsGenerateReportModalOpen(true);
                }}
                style={{ margin: 0 }}
              >
                <IconTextMenuButtonOption
                  iconName={FAIconName.filePlusRegular}
                  title={GENERATE_PATIENT_REPORT}
                />
              </ChakraMenuItem>
            )}
            {isOnPatientProfile &&
              hasSchedulingAuthorization &&
              isBookingEnabled && (
                <ChakraMenuItem
                  onClick={handleScheduleAppointmentClick}
                  style={{ margin: 0 }}
                >
                  <IconTextMenuButtonOption
                    iconName={FAIconName.calendarRegular}
                    title={SCHEDULE_APPOINTMENT_OPTION}
                  />
                </ChakraMenuItem>
              )}
            {shouldShowOrderLabButton(
              currentUser?.providerTypeName || '',
              patient.universalStatusCode,
            ) && (
              <ChakraMenuItem
                onClick={() => {
                  trackEvent(
                    PatientActionEvents.orderLabOptionActionMenuButtonClicked,
                    {
                      Source: AnalyticsSourceValues.ACTION_MENU,
                    },
                  );
                  setIsOrderLabDrawerOpen(!isOrderLabDrawerOpen);
                }}
                style={{ margin: 0 }}
              >
                <IconTextMenuButtonOption
                  iconName={FAIconName.vialRegular}
                  title={ORDER_LAB_TEXT}
                />
              </ChakraMenuItem>
            )}
          </ChakraFlex>
        </ChakraMenuList>
      </ChakraMenu>
      <ConfirmModal
        body={`${patientPreferredFullName} does not have an applicable diagnosis on file for this assessment. Overriding this restriction should only be done if you intend to use the data in support of adding the diagnosis. If this action was taken in error, please dismiss this notice.`}
        cancelLabel="Dismiss"
        confirmLabel="Override"
        confirmVariant="accent.critical"
        isOpen={isOverrideModalOpen}
        onClose={() => setOpenOverrideModal(false)}
        onConfirm={handleConfirmClick}
        title="Are you sure you wish to send this assessment?"
      />
      <Scheduler
        careTeamMembers={patientCareTeams ?? []}
        id="schedule-appointment-modal"
        isOpen={isScheduleAppointmentsOpen}
        key={schedulerKey}
        onAppointmentTypeSelected={() => setIsAnyAppointmentTypeSelected(true)}
        onClose={handleSchedulerModalClose}
        onError={onSchedulerError}
        patient={patient}
        programName={patientClinicalProfile?.programName}
        showLoadingSpinner={false}
      />
      {isOrderLabDrawerOpen && (
        <OrderLabDrawer
          closeDrawer={() => setIsOrderLabDrawerOpen(false)}
          emptyFields={getEmptyLabOrderFields(patient)}
          isOpen={isOrderLabDrawerOpen}
          onLabOrderSubmitted={handleLabOrderSubmittedActionCta}
          patientId={patient.userId}
        />
      )}
      <DiscardChangesModalV2
        isOpen={isConfirmScheduleAppointmentModalOpen}
        onClose={() => setIsConfirmScheduleAppointmentModalOpen(false)}
        onConfirm={closeSchedulerModal}
      />
      {showActionBar && (
        <LabOrderActionBar
          onHide={() => setShowActionBar(false)}
          patientId={patient.userId}
        />
      )}
      {isDocGenTreatmentSummaryEnabled && (
        <GeneratePatientReportModal
          isOpen={isGenerateReportModalOpen}
          onClose={() => setIsGenerateReportModalOpen(false)}
          patientExternalId={patient.userId}
          patientMrn={patient.mrn}
        />
      )}
      {isUploadSuccessModalOpen && (
        <UploadConfirmationModal
          id={TOP_LEVEL_ID}
          isOpen={isUploadSuccessModalOpen}
          onClose={() => setIsUploadSuccessModalOpen(false)}
          patientId={patient.userId}
          uploadedFiles={uploadedFiles}
          uploadErrorMessages={fileUploadErrorMessages}
        />
      )}
    </>
  );
};

export default ActionsCta;
